import { useState, useRef, useEffect } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { F, fC, fN, r2, quarterly, SECTIONS, SOURCES, provenanceFor, LAST_SYNC_MIN } from '@/dashboard/data';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

/* Shared small UI */
export function Dot({ ok, col }) {
  const { colors: T } = useTheme();
  return (
    <span style={{ width:6, height:6, borderRadius:'50%', background:ok?(col||'#06D6A0'):T.dim,
      display:'inline-block', animation:ok?'pulse 2s infinite':'none', flexShrink:0 }} />
  );
}

export function Tag({ col, children }) {
  return (
    <span style={{ fontSize:11, fontFamily:F.mono, fontWeight:600, padding:'2px 8px', borderRadius:4,
      background:`${col}18`, border:`1px solid ${col}40`, color:col, textTransform:'uppercase',
      letterSpacing:'0.07em', whiteSpace:'nowrap' }}>{children}</span>
  );
}

export function SecHead({ title, accent='#00D4FF', right }) {
  const { colors: T } = useTheme();
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ width:4, height:22, borderRadius:2, background:accent,
          boxShadow:`0 0 8px ${accent}66`, display:'inline-block', flexShrink:0 }} />
        <span style={{ fontSize:14, fontWeight:700, color:T.text, textTransform:'uppercase',
          letterSpacing:'0.1em', fontFamily:F.head }}>{title}</span>
      </div>
      {right && <span style={{ fontSize:12, color:T.muted, fontFamily:F.mono, fontWeight:500 }}>{right}</span>}
    </div>
  );
}

export function CT({ active, payload, label }) {
  const { colors: T } = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:T.tooltipBg, border:`1px solid ${T.tooltipBorder}`, borderRadius:8,
      padding:'12px 16px', fontFamily:F.mono, fontSize:13, minWidth:180, pointerEvents:'none',
      boxShadow:T.elev3 }}>
      <div style={{ color:T.muted, marginBottom:8, fontSize:12, fontWeight:500 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color:p.color||T.textSecondary, marginBottom:3, fontSize:13 }}>
          {p.name}: <span style={{ color:T.white, fontWeight:600 }}>
            {typeof p.value==='number' && p.value>99999 ? fC(p.value) :
             typeof p.value==='number' ? p.value.toLocaleString() : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Metric Card ── */
export function MetCard({ label, value, sub, col='#00D4FF', trend, icon, chartData, chartKey, valueFormat='auto', dateRange }) {
  const [open, setOpen] = useState(false);
  const { colors: T, isDark } = useTheme();
  const tCol = trend >= 0 ? '#06D6A0' : '#FF4444';
  const sparkId = `spark-${label.replace(/\s+/g,'-')}`;
  const prov = provenanceFor(label);
  const hasSpark = Array.isArray(chartData) && chartData.length && chartKey;

  return (
    <>
      <div data-testid={`metric-card-${label.toLowerCase().replace(/\s+/g,'-')}`}
        role="button" tabIndex={0}
        aria-label={`${label}: ${value}. Open sources and formula.`}
        title={prov ? `${prov.formula}\nSources: ${prov.sources.join(', ')}\nSynced ${LAST_SYNC_MIN} min ago` : undefined}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
        onClick={() => setOpen(true)}
        style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, borderTop:`3px solid ${col}`,
          borderRadius:10, padding:'16px 18px 10px', cursor:'pointer', position:'relative', overflow:'hidden',
          boxShadow:T.elev1, fontVariantNumeric:'tabular-nums',
          transition:'transform .18s ease, box-shadow .18s ease, background-color .18s ease', animation:'fadeUp .4s ease' }}
        onMouseEnter={e => { e.currentTarget.style.background = T.hoverBg; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = T.elev2; }}
        onMouseLeave={e => { e.currentTarget.style.background = T.surfaceAlpha; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = T.elev1; }}>
        <div style={{ position:'absolute', inset:0, background:`radial-gradient(ellipse at top left,${col}0a,transparent 65%)`, pointerEvents:'none' }} />
        {icon && <div style={{ position:'absolute', top:10, right:12, fontSize:20, opacity:.12 }}>{icon}</div>}
        <div style={{ fontSize:11, color:T.textSecondary, textTransform:'uppercase', letterSpacing:'.08em',
          marginBottom:6, fontFamily:F.mono, fontWeight:600 }}>{label}</div>
        <div style={{ fontSize:26, fontWeight:800, color:T.cardText, fontFamily:F.mono, lineHeight:1.1,
          textShadow: isDark ? '0 1px 8px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.06)' }}>{value}</div>
        {sub && <div style={{ fontSize:12, color:T.muted, marginTop:5, fontFamily:F.mono, fontWeight:500 }}>{sub}</div>}
        {trend !== undefined && <div style={{ fontSize:11, color:tCol, marginTop:4, fontFamily:F.mono, fontWeight:600 }}>
          {trend >= 0 ? '\u25B2' : '\u25BC'} {Math.abs(trend)}% vs prev
        </div>}
        {/* Inline 12-month sparkline */}
        {hasSpark && (
          <div data-testid={`spark-${label.toLowerCase().replace(/\s+/g,'-')}`}
            style={{ marginTop:8, marginLeft:-8, marginRight:-8, height:32, pointerEvents:'none' }}>
            <ResponsiveContainer width="100%" height={32}>
              <AreaChart data={chartData} margin={{ top:2, right:2, bottom:0, left:2 }}>
                <defs>
                  <linearGradient id={sparkId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={col} stopOpacity={0.35}/>
                    <stop offset="100%" stopColor={col} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey={chartKey} stroke={col} strokeWidth={1.6} fill={`url(#${sparkId})`} isAnimationActive={false} dot={false}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        <div style={{ position:'absolute', bottom:6, right:10, fontSize:10, color:T.dim, fontFamily:F.mono, fontWeight:500 }}>
          synced {LAST_SYNC_MIN}m ago
        </div>
      </div>
      {open && <CardModal label={label} value={value} col={col} sub={sub} trend={trend}
        chartData={chartData} chartKey={chartKey} valueFormat={valueFormat} dateRange={dateRange}
        onClose={() => setOpen(false)} />}
    </>
  );
}

/* ── Card Modal ── */
function CardModal({ label, value, col, sub, trend, chartData, chartKey, valueFormat='auto', dateRange, onClose }) {
  const { colors: T, isDark } = useTheme();
  const tCol = trend >= 0 ? '#06D6A0' : '#FF4444';
  const cardRef = useRef(null);
  const prov = provenanceFor(label);
  const liveCount = SOURCES.filter(s => s.ok).length;
  const offSources = SOURCES.filter(s => !s.ok).map(s => s.name);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Decide how to display values in the quarterly table based on the metric's nature.
  // 'currency' uses fC, 'count' uses fN, 'pct' renders %, otherwise infer.
  const fmtVal = (v) => {
    if (valueFormat === 'currency') return fC(v);
    if (valueFormat === 'count') return fN(v);
    if (valueFormat === 'pct') return `${r2(v)}%`;
    if (valueFormat === 'ratio') return `${r2(v)}x`;
    if (typeof v === 'number' && v > 9999) return fC(v);
    if (typeof v === 'number') return String(r2(v));
    return v;
  };

  // For pct/ratio metrics, quarterly = avg (not sum). For counts/currency, sum.
  const isAvg = valueFormat === 'pct' || valueFormat === 'ratio';
  const q = chartData && chartKey ? quarterly(chartData, chartKey, isAvg ? 'avg' : 'sum') : [];
  const ytdTotal = chartData && chartKey
    ? (isAvg
        ? r2(chartData.reduce((a,m)=>a+(+m[chartKey]||0),0)/chartData.length)
        : r2(chartData.reduce((a,m)=>a+(+m[chartKey]||0),0)))
    : null;

  const shareText = `${label}\nValue: ${value}\n${sub ? `Detail: ${sub}\n` : ''}${trend !== undefined ? `Trend: ${trend >= 0 ? '\u25B2' : '\u25BC'} ${Math.abs(trend)}% vs prev\n` : ''}\nOmniView Dashboard`;
  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
  const shareEmail = () => window.open(`mailto:?subject=Dashboard: ${label}&body=${encodeURIComponent(shareText)}`, '_blank');
  const copyText = () => navigator.clipboard?.writeText(shareText);
  const shareImage = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, { backgroundColor: T.modalBg, scale: 2 });
      canvas.toBlob(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${label.replace(/\s+/g, '_')}.png`; a.click();
        URL.revokeObjectURL(url);
      });
    } catch(e) { console.error(e); }
  };

  return (
    <div onClick={onClose} data-testid="card-modal-overlay"
      style={{ position:'fixed', inset:0, background:T.modalOverlay, zIndex:9999,
        display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)' }}>
      <div ref={cardRef} onClick={e => e.stopPropagation()}
        style={{ background:T.modalBg, border:`1px solid ${col}44`, borderTop:`3px solid ${col}`,
          borderRadius:12, width:'min(680px,92vw)', maxHeight:'88vh', overflow:'auto',
          animation:'modalIn .22s ease', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${T.border}`,
          display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div>
            <div style={{ fontSize:13, color:T.muted, textTransform:'uppercase', letterSpacing:'.1em',
              fontFamily:F.mono, marginBottom:4, fontWeight:600 }}>{label}</div>
            <div style={{ fontSize:40, fontWeight:800, color:T.cardText, fontFamily:F.mono, lineHeight:1 }}>{value}</div>
            {sub && <div style={{ fontSize:13, color:T.muted, marginTop:6, fontFamily:F.mono, fontWeight:500 }}>{sub}</div>}
            {trend !== undefined && <div style={{ fontSize:14, color:tCol, marginTop:5, fontFamily:F.mono, fontWeight:600 }}>
              {trend >= 0 ? '\u25B2' : '\u25BC'} {Math.abs(trend)}% vs previous period
            </div>}
          </div>
          <button data-testid="card-modal-close" onClick={onClose}
            style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, color:T.muted,
              fontSize:18, cursor:'pointer', borderRadius:6, width:34, height:34,
              display:'flex', alignItems:'center', justifyContent:'center' }}>x</button>
        </div>
        {chartData && (
          <div style={{ padding:'20px 24px', borderBottom:`1px solid ${T.border}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <span style={{ fontSize:12, color:T.muted, fontFamily:F.mono,
                textTransform:'uppercase', letterSpacing:'.1em', fontWeight:600 }}>Last 12 Months</span>
              {ytdTotal !== null && (
                <span style={{ fontSize:12, color:T.text, fontFamily:F.mono, fontWeight:700 }}>
                  YTD {isAvg ? 'avg' : 'total'}: <span style={{color:col}}>{fmtVal(ytdTotal)}</span>
                </span>
              )}
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="mg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={col} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={col} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.gridLine} />
                <XAxis dataKey="month" tick={{ fill:T.axisTick, fontSize:10, fontFamily:F.mono }} />
                <YAxis tick={{ fill:T.axisTick, fontSize:10, fontFamily:F.mono }} tickFormatter={v => typeof v==='number' && v>9999 ? fC(v) : (typeof v==='number' ? String(r2(v)) : v)} />
                <Tooltip content={<CT />} />
                <Area type="monotone" dataKey={chartKey||'gmv'} name={label} stroke={col} strokeWidth={2.5} fill="url(#mg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        {/* Where this number comes from — formula, sources, freshness */}
        <div data-testid="provenance-panel" style={{ padding:'16px 24px', borderBottom:`1px solid ${T.border}`, background:T.surfaceAlpha }}>
          <div style={{ fontSize:12, color:T.muted, fontFamily:F.mono, marginBottom:10,
            textTransform:'uppercase', letterSpacing:'.1em', fontWeight:600 }}>How this is calculated</div>
          <div data-testid="provenance-formula" style={{ fontSize:13, color:T.text, fontFamily:F.mono, marginBottom:10, lineHeight:1.5 }}>
            {prov?.formula || 'Derived from the connected platform data for the selected period.'}
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
            {(prov?.sources || ['Connected platforms']).map(s => (
              <span key={s} style={{ fontSize:11, fontFamily:F.mono, padding:'3px 8px', borderRadius:4,
                background:`${col}14`, border:`1px solid ${col}33`, color:T.text }}>{s}</span>
            ))}
          </div>
          <div data-testid="provenance-freshness" style={{ fontSize:12, color:T.muted, fontFamily:F.mono, lineHeight:1.6 }}>
            {liveCount} of {SOURCES.length} sources connected {'\u00B7'} synced {LAST_SYNC_MIN} min ago {'\u00B7'} {dateRange || 'selected period'}
            <br/>
            <span style={{ color:'#FFD166' }}>Excludes {offSources.join(', ')} — not connected.</span>
          </div>
        </div>
        {/* Quarterly summary */}
        {q.length > 0 && (
          <div data-testid="quarterly-summary" style={{ padding:'18px 24px', borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontSize:12, color:T.muted, fontFamily:F.mono, marginBottom:12,
              textTransform:'uppercase', letterSpacing:'.1em', fontWeight:600 }}>
              Quarterly breakdown — {isAvg ? 'average' : 'combined total'}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8 }}>
              {q.map(item => (
                <div key={item.q} data-testid={`quarter-${item.q.toLowerCase()}`}
                  style={{ background:`${col}10`, border:`1px solid ${col}33`, borderRadius:8, padding:'10px 12px' }}>
                  <div style={{ fontSize:10, color:T.muted, fontFamily:F.mono, fontWeight:600,
                    textTransform:'uppercase', letterSpacing:'.08em' }}>{item.q}</div>
                  <div style={{ fontSize:11, color:T.dim, fontFamily:F.mono, marginBottom:4 }}>{item.label}</div>
                  <div style={{ fontSize:18, fontWeight:800, color:T.cardText, fontFamily:F.mono, lineHeight:1.1 }}>{fmtVal(item.value)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ padding:'16px 24px' }}>
          <div style={{ fontSize:12, color:T.muted, fontFamily:F.mono, marginBottom:10,
            textTransform:'uppercase', letterSpacing:'.1em', fontWeight:600 }}>Share this metric</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {[
              { label:'WhatsApp', fn:shareWA, bg:'rgba(37,211,102,.12)', bc:'rgba(37,211,102,.3)', c:'#25D366' },
              { label:'Email', fn:shareEmail, bg:'rgba(66,133,244,.12)', bc:'rgba(66,133,244,.3)', c:'#4285F4' },
              { label:'Image', fn:shareImage, bg:'rgba(199,125,255,.12)', bc:'rgba(199,125,255,.3)', c:'#C77DFF' },
              { label:'Copy', fn:copyText, bg:T.surfaceAlpha, bc:T.border, c:T.muted },
            ].map(s => (
              <button key={s.label} data-testid={`share-${s.label.toLowerCase()}`} onClick={s.fn}
                style={{ display:'flex', alignItems:'center', gap:7, padding:'10px 18px', borderRadius:6,
                  background:s.bg, border:`1px solid ${s.bc}`, color:s.c, fontSize:13,
                  cursor:'pointer', fontFamily:F.mono, fontWeight:600 }}>{s.label}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Share Report Modal ── */
export function ShareModal({ data, brand, dateRange, shareUrl, onClose }) {
  const { colors: T } = useTheme();
  const k = data.kpis;
  const reportRef = useRef(null);
  const text = `OmniView - Marketing Report\nBrand: ${brand.name}\nPeriod: ${dateRange}\n\n` +
    `Total GMV: ${fC(k.totalGMV)}\nAd Spend: ${fC(k.totalSpend)}\n` +
    `Blended ROAS: ${k.blendedROAS}x\nROI: ${k.blendedROI}%\n` +
    `Orders: ${fN(k.totalOrders)}\nAvg SOV: ${k.avgSOV}%\n` +
    `NTB Rate: ${k.totalNTB}%\nLTV:CAC: ${k.ltvCac}x\n` +
    `Pipeline: ${fC(k.pipelineValue)}\nRevenue Leakage: ${fC(k.revenueLeakage)}\n\n` +
    (shareUrl ? `View this dashboard: ${shareUrl}\n\n` : '') +
    `Generated by OmniView Agency Command Center`;

  const shareImage = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(reportRef.current, { backgroundColor: T.modalBg, scale: 2 });
      canvas.toBlob(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `OmniView_Report_${brand.name}.png`; a.click();
        URL.revokeObjectURL(url);
      });
    } catch(e) { console.error(e); }
  };

  const downloadPDF = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      const canvas = await html2canvas(reportRef.current, { backgroundColor: T.modalBg, scale: 2 });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, w, h);
      pdf.save(`OmniView_Report_${brand.name}.pdf`);
    } catch(e) { console.error(e); }
  };


  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} data-testid="share-modal-overlay"
      style={{ position:'fixed', inset:0, background:T.modalOverlay, zIndex:9999,
        display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(8px)' }}>
      <div onClick={e => e.stopPropagation()} ref={reportRef}
        style={{ background:T.modalBg, border:`1px solid rgba(0,212,255,.25)`,
          borderTop:'3px solid #00D4FF', borderRadius:12, width:'min(560px,92vw)',
          animation:'modalIn .22s ease', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${T.border}`,
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:16, fontWeight:700, color:T.cardText, fontFamily:F.head }}>Share Report</div>
          <button data-testid="share-modal-close" onClick={onClose}
            style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, color:T.muted,
              fontSize:18, cursor:'pointer', borderRadius:6, width:32, height:32,
              display:'flex', alignItems:'center', justifyContent:'center' }}>x</button>
        </div>
        <div style={{ padding:'20px 24px' }}>
          <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
            {[
              { label:'WhatsApp', fn:() => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`,'_blank'), c:'#25D366', bg:'rgba(37,211,102,.1)', bc:'rgba(37,211,102,.3)' },
              { label:'Email', fn:() => window.open(`mailto:?subject=Report - ${brand.name}&body=${encodeURIComponent(text)}`,'_blank'), c:'#4285F4', bg:'rgba(66,133,244,.1)', bc:'rgba(66,133,244,.3)' },
              { label:'Image', fn:shareImage, c:'#C77DFF', bg:'rgba(199,125,255,.1)', bc:'rgba(199,125,255,.3)' },
              { label:'PDF', fn:downloadPDF, c:'#FF6B6B', bg:'rgba(255,107,107,.1)', bc:'rgba(255,107,107,.3)' },
              { label:'Copy', fn:() => navigator.clipboard?.writeText(text), c:T.muted, bg:T.surfaceAlpha, bc:T.border },
            ].map(s => (
              <button key={s.label} data-testid={`report-share-${s.label.toLowerCase()}`} onClick={s.fn}
                style={{ flex:s.label==='Copy'?'0 0 auto':'1', padding:'14px', borderRadius:7,
                  background:s.bg, border:`1px solid ${s.bc}`, color:s.c, fontSize:14,
                  cursor:'pointer', fontFamily:F.mono, fontWeight:600,
                  display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>{s.label}</button>
            ))}
          </div>
          {shareUrl && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, color:T.muted, fontFamily:F.mono, marginBottom:6,
                textTransform:'uppercase', letterSpacing:'.08em', fontWeight:600 }}>Link to this exact view</div>
              <div style={{ display:'flex', gap:6 }}>
                <input data-testid="share-url" readOnly value={shareUrl}
                  onFocus={e => e.target.select()}
                  style={{ flex:1, padding:'9px 11px', borderRadius:6, border:`1px solid ${T.border}`,
                    background:T.surfaceAlpha, color:T.text, fontSize:12, fontFamily:F.mono, minWidth:0 }} />
                <button data-testid="copy-share-url" onClick={() => navigator.clipboard?.writeText(shareUrl)}
                  style={{ padding:'9px 14px', borderRadius:6, border:'1px solid rgba(0,212,255,.35)',
                    background:'rgba(0,212,255,.1)', color:'#00A8CC', fontSize:12, fontFamily:F.mono,
                    fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>Copy link</button>
              </div>
            </div>
          )}
          <div style={{ padding:'12px 14px', background:T.surfaceAlpha, borderRadius:7,
            border:`1px solid ${T.border}`, fontSize:12, color:T.textSecondary,
            fontFamily:F.mono, lineHeight:1.8, whiteSpace:'pre-line', maxHeight:260, overflowY:'auto' }}>{text}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Sheets Panel ── */
export function SheetsPanel({ onClose }) {
  const { colors: T } = useTheme();
  const [step, setStep] = useState(0);
  const steps = [
    { title:'Create your Google Sheet', body:'Make a copy of the OmniView Master Template. Each tab = one data source.',
      code:'Sheet URL: docs.google.com/spreadsheets/d/[YOUR_SHEET_ID]' },
    { title:'Publish to Web', body:'File > Share > Publish to Web > CSV format per tab.',
      code:'File > Share > Publish to Web > Tab > CSV > Copy Link' },
    { title:'Connect Dashboard', body:'Use Apps Script, Sheets API v4, or paste CSV links.',
      code:'// Apps Script (every 5 min)\nfunction sync() {\n  const data = SpreadsheetApp.getActiveSheet().getDataRange().getValues();\n  UrlFetchApp.fetch(URL, {method:"POST", payload: JSON.stringify(data)});\n}' },
    { title:'Column Mapping', body:'Your sheet columns must match these headers:',
      code:'Platform | Spend | Revenue | Impressions | Clicks\nConversions | ROAS | ACOS | TACOS | SOV | Date' },
    { title:'Backend Options', body:'Powered by: Google Sheets (5-min), REST API, BigQuery, Supermetrics',
      code:'const res = await fetch(CSV_URL);\nconst rows = (await res.text()).split("\\n").map(r => r.split(","));' },
  ];
  const s = steps[step];

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:T.modalOverlay,
      zIndex:9999, display:'flex', alignItems:'flex-end', justifyContent:'flex-end', backdropFilter:'blur(6px)' }}>
      <div onClick={e => e.stopPropagation()} data-testid="sheets-panel"
        style={{ background:T.modalBg, borderLeft:`1px solid rgba(15,157,88,.3)`,
          width:'min(480px,95vw)', height:'100vh', animation:'slideRight .25s ease',
          overflowY:'auto', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'18px 20px', borderBottom:`1px solid ${T.border}`,
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:13, fontWeight:700, color:T.cardText, fontFamily:F.head }}>Google Sheets Integration</div>
          <button data-testid="sheets-close" onClick={onClose}
            style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, color:T.muted,
              fontSize:16, cursor:'pointer', borderRadius:5, width:30, height:30,
              display:'flex', alignItems:'center', justifyContent:'center' }}>x</button>
        </div>
        <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
          <div style={{ display:'flex', gap:4 }}>
            {steps.map((_, i) => (
              <div key={i} onClick={() => setStep(i)}
                style={{ flex:1, height:3, borderRadius:2, cursor:'pointer',
                  background:i <= step ? '#0F9D58' : T.surfaceAlpha, transition:'background .2s' }} />
            ))}
          </div>
          <div style={{ fontSize:9, color:T.dim, fontFamily:F.mono, marginTop:6 }}>Step {step+1}/{steps.length}</div>
        </div>
        <div style={{ padding:20, flex:1 }}>
          <div style={{ fontSize:16, fontWeight:700, color:T.cardText, fontFamily:F.head, marginBottom:10 }}>{s.title}</div>
          <div style={{ fontSize:14, color:T.textSecondary, lineHeight:1.7, marginBottom:14 }}>{s.body}</div>
          <div style={{ background:T.codeBg, border:'1px solid rgba(15,157,88,.2)', borderRadius:7,
            padding:'12px 14px', fontFamily:F.mono, fontSize:12, color:'#0F9D58',
            lineHeight:1.7, whiteSpace:'pre-wrap' }}>{s.code}</div>
        </div>
        <div style={{ padding:'16px 20px', borderTop:`1px solid ${T.border}`, display:'flex', gap:8 }}>
          <button data-testid="sheets-prev" onClick={() => setStep(Math.max(0, step-1))} disabled={step===0}
            style={{ flex:1, padding:10, borderRadius:6, background:T.surfaceAlpha,
              border:`1px solid ${T.border}`, color:step===0 ? T.dim : T.muted,
              fontSize:12, cursor:step===0?'not-allowed':'pointer', fontFamily:F.mono }}>Prev</button>
          <button data-testid="sheets-next" onClick={() => setStep(Math.min(steps.length-1, step+1))}
            disabled={step===steps.length-1}
            style={{ flex:2, padding:10, borderRadius:6, background:'rgba(15,157,88,.15)',
              border:'1px solid rgba(15,157,88,.3)', color:'#0F9D58',
              fontSize:12, cursor:step===steps.length-1?'not-allowed':'pointer', fontFamily:F.mono, fontWeight:600 }}>
            {step===steps.length-1 ? 'Done' : 'Next Step'}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Client Panel ── */
export function ClientPanel({ visible, onToggle, onClose }) {
  const { colors: T } = useTheme();
  const byTab = {};
  SECTIONS.forEach(s => { if (!byTab[s.tab]) byTab[s.tab] = []; byTab[s.tab].push(s); });

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:T.modalOverlay,
      zIndex:9999, display:'flex', alignItems:'flex-start', justifyContent:'flex-end', backdropFilter:'blur(5px)' }}>
      <div onClick={e => e.stopPropagation()} data-testid="client-panel"
        style={{ background:T.modalBg, borderLeft:`1px solid rgba(255,209,102,.3)`,
          width:'min(380px,90vw)', height:'100vh', animation:'slideRight .25s ease',
          overflowY:'auto', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'18px 20px', borderBottom:`1px solid ${T.border}`,
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:13, fontWeight:700, color:T.cardText, fontFamily:F.head }}>Client View Controls</div>
          <button data-testid="client-panel-close" onClick={onClose}
            style={{ background:T.surfaceAlpha, border:`1px solid ${T.border}`, color:T.muted,
              fontSize:16, cursor:'pointer', borderRadius:5, width:30, height:30,
              display:'flex', alignItems:'center', justifyContent:'center' }}>x</button>
        </div>
        <div style={{ padding:20, flex:1 }}>
          <div style={{ fontSize:12, color:T.textSecondary, fontFamily:F.mono, marginBottom:14, lineHeight:1.6 }}>
            Toggle sections visible to your client. Hidden sections won&apos;t appear in client view.
          </div>
          {Object.entries(byTab).map(([tab, sections]) => (
            <div key={tab} style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, color:T.muted, fontFamily:F.mono, textTransform:'uppercase',
                letterSpacing:'.1em', marginBottom:8, fontWeight:600 }}>{tab}</div>
              {sections.map(s => (
                <div key={s.id} onClick={() => onToggle(s.id)} data-testid={`client-toggle-${s.id}`}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', cursor:'pointer',
                    borderRadius:5, marginBottom:3, background:visible[s.id]!==false ? 'rgba(6,214,160,.06)' : T.surfaceAlpha,
                    border:`1px solid ${visible[s.id]!==false ? 'rgba(6,214,160,.15)' : T.border}` }}>
                  <div style={{ width:14, height:14, borderRadius:3,
                    background:visible[s.id]!==false ? '#06D6A0' : T.surfaceAlpha,
                    border:`1px solid ${visible[s.id]!==false ? '#06D6A0' : T.border}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:10, color:'#fff', fontWeight:700 }}>
                    {visible[s.id]!==false && '\u2713'}
                  </div>
                  <span style={{ fontSize:13, color:visible[s.id]!==false ? T.text : T.dim, fontFamily:F.mono }}>{s.label}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
